'use client';

import { useMemo, useState } from 'react';

import { getPrimaryImage } from '@/lib/entity-image';
import { getAllClasses } from '@/helpers/class';
import { detailPath } from '@/helpers/offering';
import { useOpenSitePage } from '@/hooks/use-open-site-page';
import useClassGroups, { Class } from '@/state/class-groups';
import { ClassRowCard } from './class-row-card';
import { ShowMoreLink } from './show-more-link';
import { Separator } from '@mokoboko/components';

const VISIBLE_LIMIT = 4;

type ProductType =
  | 'class-pack'
  | 'subscription'
  | 'gift-card'
  | 'gift-card-money';

const SECTION_COPY: Record<
  ProductType,
  { titleOne: string; titleMany: string; one: string; many: string }
> = {
  'class-pack': {
    titleOne: 'Class included',
    titleMany: 'Classes included',
    one: 'Use your pack for this class',
    many: 'Use your pack across any of these classes',
  },
  subscription: {
    titleOne: 'Class included',
    titleMany: 'Classes included',
    one: 'Use your membership for this class',
    many: 'Use your membership across any of these classes',
  },
  'gift-card': {
    titleOne: 'Classes you can redeem it for',
    titleMany: 'Classes you can redeem it for',
    one: 'The person you give it to chooses the time',
    many: 'The person you give it to chooses the class and the time',
  },
  // A money card is spent, not redeemed (gift cards M1).
  'gift-card-money': {
    titleOne: 'Classes it pays for',
    titleMany: 'Classes it pays for',
    one: 'The person you give it to chooses the time',
    many: 'The person you give it to chooses the class and the time',
  },
};

export const PickYourClassSection = ({
  classIds,
  productType,
}: {
  classIds: string[];
  productType: ProductType;
}) => {
  const openPage = useOpenSitePage();
  const [expanded, setExpanded] = useState(false);
  const { data: classGroups } = useClassGroups({ include_classes: true });

  const openClass = (slug: string) =>
    openPage(detailPath('class', slug), 'Allow pop-ups to open this class.');

  const classMap = useMemo(
    () =>
      new Map(
        getAllClasses(classGroups ?? []).map(
          (cls) => [cls.class_id, cls] as const
        )
      ),
    [classGroups]
  );

  const classes = classIds
    .map((id) => classMap.get(id))
    .filter((cls): cls is Class => Boolean(cls));

  if (classes.length === 0) return null;

  const visibleClasses = expanded ? classes : classes.slice(0, VISIBLE_LIMIT);
  const hasMore = classes.length > VISIBLE_LIMIT;

  const copy = SECTION_COPY[productType];

  return (
    <div className="flex flex-col gap-6">
      <Separator />
      <section className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h2 className="heading-section">
            {classes.length === 1 ? copy.titleOne : copy.titleMany}
          </h2>
          <p className="text-sm text-gray-700">
            {classes.length === 1 ? copy.one : copy.many}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-x-5 sm:gap-y-4">
            {visibleClasses.map((cls) => (
              <ClassRowCard
                key={cls.class_id}
                name={cls.name}
                imageUrl={getPrimaryImage(cls.images)?.url ?? cls.image}
                onClick={() => openClass(cls.slug)}
              />
            ))}
          </div>

          {hasMore ? (
            <ShowMoreLink
              expanded={expanded}
              hiddenCount={classes.length - VISIBLE_LIMIT}
              onToggle={() => setExpanded((value) => !value)}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
};
